import React, { useEffect, useState } from "react";
import { connect } from "react-redux";
import { Link } from "react-router-dom";
import {
  Avatar,
  Box,
  Button,
  CircularProgress,
  Container,
  Paper,
  TextField,
  Typography,
  Alert,
} from "@mui/material";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
// @ts-ignore
import ReCAPTCHA from "react-google-recaptcha";
import { login } from "../../actions/auth";
import TokenService from "../../services/token";
import rutas from "../../routes/constants/routes";

const { REACT_APP_CAPTCHA_APIKEY } = process.env;

/* -------------------------------------------------------------------------- */
/*                                   Types                                    */
/* -------------------------------------------------------------------------- */

interface DocType {
  id: string | number;
  name: string;
}

interface ApiError {
  response?: {
    data?: string;
  };
}

interface LoginFormValues {
  identification: string;
  email: string;
  password: string;
  captchaToken: string;
}

type LoginFormErrors = Partial<Record<keyof LoginFormValues, string>>;

type LoginFormTouched = Partial<Record<keyof LoginFormValues, boolean>>;

interface LoginStateProps {
  session?: unknown;
  success?: boolean;
  loading?: boolean;
  data?: unknown;
  docType?: DocType[];
  error?: ApiError;
}

interface LoginDispatchProps {
  login: (loginData: {
    identification: string;
    email: string;
    password: string;
    captchaToken: string;
  }) => void;
}

type LoginProps = LoginStateProps & LoginDispatchProps;

const CUIT_REGEX = /^\d{2}-\d{8}-\d{1}$/;

const INITIAL_VALUES: LoginFormValues = {
  identification: "",
  email: "",
  password: "",
  captchaToken: "",
};

/* -------------------------------------------------------------------------- */
/*                                Component                                   */
/* -------------------------------------------------------------------------- */

const Login: React.FC<LoginProps> = (props) => {
  const [values, setValues] = useState<LoginFormValues>(INITIAL_VALUES);
  const [errors, setErrors] = useState<LoginFormErrors>({});
  const [touched, setTouched] = useState<LoginFormTouched>({});

  useEffect(() => {
    if (props.success && TokenService.hasSession()) {
      window.location.assign(rutas.DASHBOARD);
    }
    if (TokenService.hasSession()) {
      window.location.assign(rutas.DASHBOARD);
    }
  }, [props.success]);

  const formatCuit = (value: string): string => {
    // Solo deja números
    const onlyNums = value.replace(/\D/g, "");

    if (onlyNums.length <= 2) return onlyNums;
    if (onlyNums.length <= 10)
      return `${onlyNums.slice(0, 2)}-${onlyNums.slice(2)}`;
    return `${onlyNums.slice(0, 2)}-${onlyNums.slice(2, 10)}-${onlyNums.slice(
      10,
      11,
    )}`;
  };

  const validate = (data: LoginFormValues): LoginFormErrors => {
    const validationErrors: LoginFormErrors = {};

    if (!data.identification) {
      validationErrors.identification = "El número de documento es obligatorio";
    } else if (!CUIT_REGEX.test(data.identification)) {
      validationErrors.identification =
        "Formato inválido. Debe ser xx-xxxxxxxx-x";
    }

    if (!data.email) {
      validationErrors.email = "El email es obligatorio";
    }

    if (!data.password) {
      validationErrors.password = "Ingrese su contraseña";
    }

    if (!data.captchaToken) {
      validationErrors.captchaToken = "Captcha requerido";
    }

    return validationErrors;
  };

  const handleFieldChange =
    (field: keyof LoginFormValues) =>
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const { value } = e.target;
      setValues((prev) => ({ ...prev, [field]: value }));
    };

  const handleCuitChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
    const formatted = formatCuit(e.target.value);
    setValues((prev) => ({ ...prev, identification: formatted }));
  };

  const handleBlur = (field: keyof LoginFormValues) => () => {
    setTouched((prev) => ({ ...prev, [field]: true }));
    setErrors(validate(values));
  };

  const handleCaptchaChange = (value: string | null) => {
    setValues((prev) => ({ ...prev, captchaToken: value ?? "" }));
  };

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    const validationErrors = validate(values);
    setErrors(validationErrors);
    setTouched({
      identification: true,
      email: true,
      password: true,
      captchaToken: true,
    });

    if (Object.keys(validationErrors).length === 0) {
      props.login({
        ...values,
        identification: values.identification.replace(/-/g, ""),
      });
    }
  };

  return (
    <Container component="main" maxWidth="sm">
      <Paper
        elevation={0}
        sx={{
          marginTop: 8,
          padding: 4,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
        }}
      >
        <Avatar sx={{ bgcolor: "#E3000B", width: 45, height: 45 }}>
          <LockOutlinedIcon />
        </Avatar>

        <Typography variant="h5" align="center" mb={4}>
          Complete sus datos para iniciar sesión
        </Typography>

        <Box
          component="form"
          onSubmit={handleSubmit}
          noValidate
          sx={{ width: "100%" }}
        >
          <TextField
            fullWidth
            margin="normal"
            id="email"
            name="email"
            label="Email"
            type="email"
            value={values.email}
            onChange={handleFieldChange("email")}
            onBlur={handleBlur("email")}
            error={touched.email && Boolean(errors.email)}
            helperText={touched.email && errors.email}
          />

          <TextField
            fullWidth
            margin="normal"
            id="identification"
            name="identification"
            label="CUIT/CUIL"
            value={values.identification}
            onChange={handleCuitChange}
            onBlur={handleBlur("identification")}
            error={touched.identification && Boolean(errors.identification)}
            helperText={touched.identification && errors.identification}
            inputProps={{ maxLength: 13 }}
          />

          <TextField
            fullWidth
            margin="normal"
            id="password"
            name="password"
            label="Contraseña"
            type="password"
            value={values.password}
            onChange={handleFieldChange("password")}
            onBlur={handleBlur("password")}
            error={touched.password && Boolean(errors.password)}
            helperText={touched.password && errors.password}
          />

          <Box sx={{ display: "flex", justifyContent: "center", mt: 2 }}>
            <ReCAPTCHA
              sitekey={REACT_APP_CAPTCHA_APIKEY ?? ""}
              onChange={handleCaptchaChange}
            />
          </Box>
          {touched.captchaToken && errors.captchaToken ? (
            <Typography
              variant="body2"
              color="error"
              align="center"
              sx={{ mt: 1 }}
            >
              {errors.captchaToken}
            </Typography>
          ) : null}

          {props.error ? (
            <Alert severity="error" sx={{ mt: 2 }}>
              {props.error.response?.data}
            </Alert>
          ) : null}
          <Box sx={{ display: "flex", justifyContent: "center", mt: 2 }}>
            <Button
              type="submit"
              fullWidth
              variant="contained"
              disabled={props.loading}
              sx={{
                mt: 3,
                mb: 2,
                width: "10vw",
                backgroundColor: "#E3000B",
                "&:hover": { backgroundColor: "#c40009" },
              }}
            >
              {props.loading ? (
                <CircularProgress size={24} sx={{ color: "white" }} />
              ) : (
                "Iniciar sesión"
              )}
            </Button>
          </Box>
          <Box sx={{ textAlign: "center" }}>
            <Link to={rutas.RESET_PASWORD}>¿Olvidaste tu contraseña?</Link>
          </Box>
        </Box>
      </Paper>
    </Container>
  );
};

/* -------------------------------------------------------------------------- */
/*                              Redux                                         */
/* -------------------------------------------------------------------------- */

interface RootState {
  auth: {
    session?: unknown;
    success?: boolean;
    loading?: boolean;
    data?: unknown;
    error?: ApiError;
  };
  params: {
    docType?: DocType[];
  };
}

const mapStateToProps = (state: RootState): LoginStateProps => ({
  session: state.auth.session,
  success: state.auth.success,
  loading: state.auth.loading,
  data: state.auth.data,
  docType: state.params.docType,
  error: state.auth.error,
});

const mapDispatchToProps = (dispatch: any): LoginDispatchProps => ({
  login: (loginData) => dispatch(login(loginData)),
});

export default connect(mapStateToProps, mapDispatchToProps)(Login);
